"use client";

// src/anchor.tsx
import * as React2 from "react";
import { Base, mergeStyles, systemForwardRef } from "@stepstone-genesis/system";
import { useMergedRefs } from "@stepstone-genesis/hooks-use-merged-refs";

// src/styles.ts
var focusStyles = {
  fontFamily: "fontFamilyBody",
  fontWeight: "fontWeightNormal",
  _focusVisible: {
    outline: "outlineDefault",
    boxShadow: "boxShadowFocusWhite",
    outlineOffset: "2px",
    textDecoration: "underline",
    borderRadius: "radiusM"
  }
};

// src/hooks.ts
import * as React from "react";
import { useSafeEffect } from "@stepstone-genesis/hooks-use-safe-effect";
var useLinkTypeChecker = (props) => {
  const [attributes, setAttributes] = React.useState({});
  const targetForExternal = props.target || "_blank";
  const relForExternal = props.rel;
  useSafeEffect(() => {
    if (props.href.startsWith("http")) {
      setAttributes({ rel: relForExternal, target: targetForExternal });
    } else {
      setAttributes({ rel: props.rel, target: props.target });
    }
  }, [props.href, props.rel, props.target]);
  return attributes;
};

// src/anchor.tsx
var Anchor = systemForwardRef(function Anchor2(props, ref) {
  const { href, anchorStyles = {}, ...rest } = props;
  const linkRef = React2.useRef(null);
  const mergedRef = useMergedRefs(ref, linkRef);
  const { rel, target } = useLinkTypeChecker(props);
  const styles2 = mergeStyles([focusStyles, anchorStyles]);
  return /* @__PURE__ */ React2.createElement(
    Base,
    {
      as: "a",
      href,
      ref: mergedRef,
      __themeDefinition: "anchors",
      __root: styles2,
      ...rest,
      target,
      rel
    },
    props.children
  );
});

// src/textLink/index.tsx
import * as React4 from "react";
import { systemForwardRef as systemForwardRef2 } from "@stepstone-genesis/system";
import { Flex } from "@stepstone-genesis/components-flex";
import { useMergedRefs as useMergedRefs2 } from "@stepstone-genesis/hooks-use-merged-refs";

// src/textLink/renderIcons.tsx
import * as React3 from "react";
import { ExternalLinkIcon } from "@stepstone-genesis/icons";
var renderIcon = ({
  Icon,
  size,
  space
}) => Icon ? /* @__PURE__ */ React3.createElement(Icon, { size, mr: space }) : null;

// src/textLink/styles.ts
import { mergeAll } from "@stepstone-genesis/utilities";
var root = {
  display: "inline-block",
  verticalAlign: "bottom",
  color: "colorActionableDefault",
  _hover: { color: "colorActionableHover", textDecoration: "underline" },
  _active: { color: "colorActionableActive", textDecoration: "underline" }
};
var bare = {
  textDecoration: "none"
};
var b2c = {
  color: "colorActionableB2CDefault",
  _hover: { color: "colorActionableB2CHover" },
  _active: { color: "colorActionableB2CActive" }
};
var secondary = {
  textDecoration: "none",
  color: "colorBlack",
  _hover: { color: "colorActionableHover", textDecoration: "underline" },
  _active: { color: "colorActionableActive", textDecoration: "underline" }
};
var variants = {
  standard: root,
  bare: mergeAll([root, bare]),
  standardB2C: mergeAll([root, b2c]),
  bareB2C: mergeAll([root, bare, b2c]),
  secondary
};
var styles = { variants };

// src/textLink/index.tsx
var TextLink = systemForwardRef2(
  function TextLink2(props, ref) {
    const {
      children,
      flexSxStyles,
      href,
      icon,
      iconSize = "medium",
      space = "spaceXs",
      rel,
      target,
      variant = "standard",
      ...rest
    } = props;
    const linkRef = React4.useRef(null);
    const mergedRef = useMergedRefs2(ref, linkRef);
    const textLinkStyles = styles.variants[variant];
    const flexStyles = Object.assign(
      {},
      { verticalAlign: "bottom" },
      flexSxStyles
    );
    return /* @__PURE__ */ React4.createElement(
      Anchor,
      {
        anchorStyles: textLinkStyles,
        href,
        ref: mergedRef,
        rel,
        target,
        ...rest
      },
      icon ? /* @__PURE__ */ React4.createElement(
        Flex,
        {
          as: "span",
          flxType: "inline-flex",
          flxItems: "center",
          sx: flexStyles
        },
        renderIcon({ Icon: icon, size: iconSize, space }),
        children
      ) : children
    );
  }
);
export {
  Anchor,
  TextLink,
  styles as anchorStyles,
  useLinkTypeChecker
};
